import {
  ArrowDownToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUpToLine,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Grid2x2,
  Trash2,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import type { MergeDirection, TableInfo } from "./documentEditor";

interface TablePropertiesPanelProps {
  /** What the selection inside the static table can do. */
  table: TableInfo;
  /** Inserts an empty row above or below the anchor row. */
  onInsertRow: (position: "above" | "below") => void;
  /** Removes the anchor row. */
  onRemoveRow: () => void;
  /** Inserts an empty column beside the anchor cell. */
  onInsertColumn: (side: "left" | "right") => void;
  /** Removes the anchor cell's column. */
  onRemoveColumn: () => void;
  /** Merges the anchor cell with its aligned neighbour. */
  onMergeCells: (direction: MergeDirection) => void;
  /** Splits the anchor cell's merge back into single cells. */
  onUnmergeCells: () => void;
}

/** One button in an axis row. */
interface TableOperation {
  label: string;
  icon: LucideIcon;
  disabled: boolean;
  destructive: boolean;
  onClick: () => void;
}

/** The two operations rows, each labelled by the axis it edits. */
interface TableAxis {
  label: string;
  operations: TableOperation[];
}

/**
 * Properties inspector section for a selection inside a static table: row and
 * column inserts and deletes. Row operations anchor on the selected row (or the
 * selected cell's row) and column operations on the selected cell, so each
 * button disables when the selection cannot supply its anchor - the table itself
 * anchors nothing - and the last row and last column refuse to delete.
 *
 * The axes render as labelled rows, so the buttons line up into a grid and
 * each delete is disambiguated by the axis beside it rather than by hovering
 * two identical icons. The merge row anchors on the selected cell and each of
 * its buttons enables only when an aligned neighbour exists on that side, with
 * unmerge closing the row and enabling only on an already merged cell.
 * @param table - What the selection inside the static table can do
 * @param onInsertRow - Inserts an empty row above or below the anchor row
 * @param onRemoveRow - Removes the anchor row
 * @param onInsertColumn - Inserts an empty column beside the anchor cell
 * @param onRemoveColumn - Removes the anchor cell's column
 * @param onMergeCells - Merges the anchor cell with its aligned neighbour
 * @param onUnmergeCells - Splits the anchor cell's merge back into single cells
 * @returns The table row, column and merge operation buttons
 */
export function TablePropertiesPanel({
  table,
  onInsertRow,
  onRemoveRow,
  onInsertColumn,
  onRemoveColumn,
  onMergeCells,
  onUnmergeCells,
}: TablePropertiesPanelProps) {
  const axes: TableAxis[] = [
    {
      label: "Rows",
      operations: [
        {
          label: "Insert row above",
          icon: ArrowUpToLine,
          disabled: !table.canEditRows,
          destructive: false,
          onClick: () => onInsertRow("above"),
        },
        {
          label: "Insert row below",
          icon: ArrowDownToLine,
          disabled: !table.canEditRows,
          destructive: false,
          onClick: () => onInsertRow("below"),
        },
        {
          label: "Delete row",
          icon: Trash2,
          disabled: !table.canRemoveRow,
          destructive: true,
          onClick: onRemoveRow,
        },
      ],
    },
    {
      label: "Columns",
      operations: [
        {
          label: "Insert column left",
          icon: ArrowLeftToLine,
          disabled: !table.canEditColumns,
          destructive: false,
          onClick: () => onInsertColumn("left"),
        },
        {
          label: "Insert column right",
          icon: ArrowRightToLine,
          disabled: !table.canEditColumns,
          destructive: false,
          onClick: () => onInsertColumn("right"),
        },
        {
          label: "Delete column",
          icon: Trash2,
          disabled: !table.canRemoveColumn,
          destructive: true,
          onClick: onRemoveColumn,
        },
      ],
    },
    {
      label: "Merge",
      operations: [
        {
          label: "Merge left",
          icon: ChevronLeft,
          disabled: !table.canMergeLeft,
          destructive: false,
          onClick: () => onMergeCells("left"),
        },
        {
          label: "Merge right",
          icon: ChevronRight,
          disabled: !table.canMergeRight,
          destructive: false,
          onClick: () => onMergeCells("right"),
        },
        {
          label: "Merge up",
          icon: ChevronUp,
          disabled: !table.canMergeUp,
          destructive: false,
          onClick: () => onMergeCells("up"),
        },
        {
          label: "Merge down",
          icon: ChevronDown,
          disabled: !table.canMergeDown,
          destructive: false,
          onClick: () => onMergeCells("down"),
        },
        {
          label: "Unmerge",
          icon: Grid2x2,
          disabled: !table.canUnmerge,
          destructive: false,
          onClick: onUnmergeCells,
        },
      ],
    },
  ];
  const hint = anchorHint(table);

  return (
    <div>
      <Label className="mb-1 block text-[11px]">Table</Label>
      <div className="space-y-0.5">
        {axes.map((axis) => (
          <div key={axis.label} className="flex items-center gap-0.5">
            <span className="w-16 shrink-0 text-[11px] text-muted-foreground">
              {axis.label}
            </span>
            {axis.operations.map((operation) => (
              <Button
                key={operation.label}
                variant="ghost"
                size="icon"
                className={
                  operation.destructive
                    ? "size-8 text-destructive"
                    : "size-8 text-muted-foreground"
                }
                title={operation.label}
                aria-label={operation.label}
                disabled={operation.disabled}
                onClick={operation.onClick}
              >
                <operation.icon className="size-4" />
              </Button>
            ))}
          </div>
        ))}
      </div>
      {hint !== null && (
        <p className="mt-1.5 text-[11px] text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

/**
 * The hint naming the selection an operation still needs. Column operations
 * anchor on a cell and row operations on a row, so a row selection leaves the
 * column buttons greyed with nothing on screen explaining why.
 * @param table - What the selection inside the static table can do
 * @returns The hint, or null when both axes are anchored
 */
function anchorHint(table: TableInfo): string | null {
  if (!table.canEditRows) {
    return "Select a row or cell in the table to edit its rows and columns.";
  }
  if (!table.canEditColumns) {
    return "Select a cell to edit columns.";
  }
  return null;
}
